import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, ClipboardCheck, Send, Gift, MessageSquare, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface PosVisitaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const entreguesOptions = [
  { id: "brinde", label: "BRINDE" },
  { id: "folder", label: "FOLDER" },
  { id: "material-promocional", label: "MATERIAL PROMOCIAL" },
  { id: "outros", label: "OUTROS" },
];

export function PosVisitaModal({ open, onOpenChange }: PosVisitaModalProps) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [objetivos, setObjetivos] = useState("");

  const toggle = (id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-lg overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
        <div className="relative overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                <ClipboardCheck className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                  Pós Visita
                </DialogTitle>
                <p className="truncate text-xs font-medium text-white/70">
                  Registre o resultado da visita
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto bg-muted/30 p-4 sm:p-5">
          <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
              <div className="flex h-6 w-6 items-center justify-center rounded-md bg-tudo-blue/10">
                <Gift className="h-3.5 w-3.5 text-tudo-blue" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
                Saldos de Relacionamento Entregues
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
              {entreguesOptions.map((option) => {
                const active = !!selected[option.id];
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggle(option.id)}
                    aria-pressed={active}
                    className={`flex items-center gap-3 rounded-lg border p-3 text-left text-sm font-medium transition-all ${
                      active
                        ? "border-tudo-blue bg-tudo-blue/5 text-tudo-blue shadow-sm"
                        : "border-border bg-card text-foreground hover:border-tudo-blue/40 hover:bg-tudo-blue/5"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                        active ? "border-tudo-blue bg-tudo-blue" : "border-border"
                      }`}
                    >
                      {active && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                    </span>
                    {option.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
              <div className="flex h-6 w-6 items-center justify-center rounded-md bg-tudo-blue/10">
                <MessageSquare className="h-3.5 w-3.5 text-tudo-blue" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
                Objetivos alcançados nesta visita
              </h3>
            </div>
            <div className="p-4">
              <textarea
                value={objetivos}
                onChange={(e) => setObjetivos(e.target.value)}
                placeholder="Descreva os objetivos alcançados..."
                className="min-h-[110px] w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-tudo-blue focus:outline-none focus:ring-2 focus:ring-tudo-blue/30"
              />
            </div>
          </section>

          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-tudo-green to-tudo-green/85 py-5 text-base font-semibold text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg focus:ring-2 focus:ring-tudo-green focus:ring-offset-2"
          >
            <Send className="h-4 w-4" />
            Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
